import React, { SVGAttributes, useEffect, useState } from 'react';
import { RouteComponentProps, useLocation } from '@reach/router';
import { StaticImage } from 'gatsby-plugin-image';
import classNames from 'classnames';

import {
  BannerAlertBox,
  SmallAlertBox,
  SmallExpandableAlertBox,
} from '@entur/alert';
import {
  MapPinIcon,
  ValidationErrorFilledIcon,
  ValidationExclamationFilledIcon,
} from '@entur/icons';
import {
  Heading5,
  Heading4,
  Heading3,
  Link,
  SubParagraph,
  Paragraph,
  Heading2,
  ListItem,
  UnorderedList,
} from '@entur/typography';
import { TravelHeader, TravelTag } from '@entur/travel';
import { Transport } from '@entur/travel/dist/utils';
import { Badge, BaseCard, Tag } from '@entur/layout';
import { ExpandableText } from '@entur/expand';

import { getIncidents } from '~/lib/sanity-client';
import PortableText from '~/components/PortableText';
import ArticleHeader from '~/components/ArticleHeader';
import ContentWrapper from '~/components/ContentWrapper';
import { FullWidthBlock } from '~/components/FullWidthBlock';

import './trafikkmeldinger.scss';

import Musk from '../../images/Reisende/Trafikkmeldinger/Musk.png';

type Route = {
  transportType?: Transport;
  routeId?: string;
  routeName?: string;
};

type Incident = {
  incidentSolved: boolean;
  statusTypeField: {
    statusType: string;
  };
  incidentType: {
    predefinedIncidentType: string; //'custom'
    customIncidentType?: string; //'Bøyd skinne'
  };
  incidentStatus: {
    predefinedStatusType: string; //'custom'
    customStatusType?: string; //'Stengs'
  };
  incidentArea: {
    isArea: boolean;
    positionFrom?: string;
    positionTo?: string;
    area?: string;
  };
  incidentDescription: {
    incidentDescriptionContent: string;
    railReplacementBus: boolean;
  };
  routes?: Array<Route>;
  incidentLinks?: Array<{
    linkAdress?: string;
    linkName?: string;
  }>;
  incidentTags?: Array<{ label: string; value: string }>;
  _createdAt: string; //"2024-10-04T08:36:19Z"
  _updatedAt: string; //"2024-10-04T08:37:37Z"
};

type IconMap = {
  [key: string]: {
    icon: any;
    title: string;
    color?: string;
    props?: SVGAttributes<SVGElement>;
  };
};

const Trafikkmeldinger = (props: RouteComponentProps) => {
  const location = useLocation();
  const [incidents, setIncidents] = useState<Incident[]>([]);

  const isDraftPreview = location.search === '?draftPreview=true';

  useEffect(() => {
    async function _getIncidents() {
      const _incidents = await getIncidents({ useDraft: isDraftPreview });
      setIncidents(_incidents);
    }

    _getIncidents();
  }, []);

  return (
    <>
      <ArticleHeader
        title="Trafikkmeldinger"
        excerpt={
          <>
            <p>
              Informasjon om pågående hendelser i togtrafikken oppdatert av
              Entur sitt kundesenter.
            </p>
            <em>This page is currently only available in Norwegian.</em>
          </>
        }
        isBeta={true}
        imageSrc={Musk}
        hideMediaOnMobile
      />
      {!isDraftPreview && betaSiteWarning}
      <ContentWrapper className="incidents">
        {isDraftPreview && draftPreviewWarning}
        <IncidentList incidents={incidents} />
        <RelatedLinks />
      </ContentWrapper>
      <IncidentFooter />
    </>
  );
};

const betaSiteWarning = (
  <SmallExpandableAlertBox
    variant="information"
    title="Dette er en betaversjon"
    style={{
      marginTop: '-1rem',
      bordeRadius: 0,
      border: 'none',
      marginInline: 'calc(-50vw + 50%)',
      paddingInline: 'calc(50vw - 50%)',
    }}
  >
    Disse sidene er del av et prøveprosjekt og kan ha mangelfull eller feil
    informasjon.{' '}
    <Link href="mailto:teamdesignsystem@entur.org">
      Vi setter stor pris på tilbakemeldinger gjennom e-post!
    </Link>
    <br />
    (Vi kan dessverre ikke besvare tilbakemeldinger. Unngå å sende sensitiv
    info.)
    <br />
    <br />
    For spørsmål om din reise eller billett, kontakt kundesenteret på{' '}
    <Link href="tel:+4761279088">61 27 90 88</Link>.
  </SmallExpandableAlertBox>
);
const draftPreviewWarning = (
  <BannerAlertBox
    variant="warning"
    title="Utkast"
    style={{ maxWidth: '45rem' }}
  >
    Disse hendelsene har tekst fra utkastene. Trykk på "Publiser" i Sanity for
    at endringene skal vises for de reisende på entur.no
  </BannerAlertBox>
);

const IncidentList = (props: { incidents: Incident[] }) => {
  const { incidents } = props;

  return (
    <>
      <Heading2 as="h2">Pågående hendelser</Heading2>
      {incidents
        .filter((incident) => {
          const lastUpdated = new Date(incident._updatedAt).getTime();
          const twentyFourHoursAgo = Date.now() - 24 * 60 * 60 * 1000;

          return (
            incident.incidentSolved === false ||
            incident.incidentSolved === undefined ||
            lastUpdated > twentyFourHoursAgo
          );
        })
        ?.sort((incidentA, incidentB) => {
          if (incidentA.incidentSolved && !incidentB.incidentSolved) {
            return 1; // incidentA (solved) goes after incidentB (unsolved)
          }
          if (!incidentA.incidentSolved && incidentB.incidentSolved) {
            return -1; // incidentA (unsolved) goes before incidentB (solved)
          }
          // If both are solved or both are unsolved, sort by the most recently updated
          return new Date(incidentA._updatedAt) <=
            new Date(incidentB._updatedAt)
            ? 1
            : -1;
        })
        .map((incident, index) => (
          <IncidentCard key={incident?._id} {...incident} />
        ))}
      {incidents.length === 0 && (
        <div className="incidents__card--no-results">
          <StaticImage
            alt=""
            src="../../images/Reisende/Trafikkmeldinger/Beaver.png"
            height={240}
            style={{ width: 'fit-content' }}
          />
          <SubParagraph>
            Vi kjenner ikke til noen større hendelser i togtrafikken nå. Det
            betyr ikke nødvendigvis at alt går helt på skinner – for å være helt
            sikker bør du se på sidene til togoperatørene og Bane NOR.
          </SubParagraph>
        </div>
      )}
      <SmallAlertBox
        variant="info"
        title="Opplever du en hendelse som ikke finnes her?"
      >
        Hendelser oppdateres fortløpende av kundesenteret i Entur. Finner du
        ikke det du leter etter her eller har mer informasjon til oss, kan du
        kontakte oss på <Link href="tel:+4761279088">61 27 90 88</Link>.
      </SmallAlertBox>
    </>
  );
};

const RelatedLinks = () => (
  <>
    <Heading2>Relevante lenker</Heading2>
    <UnorderedList className="incidents__related-links">
      <ListItem>
        <Link href="https://www.vy.no/trafikk-og-ruter/trafikkinfo-for-tog-og-buss">
          Vy trafikkstatus
        </Link>
      </ListItem>
      <ListItem>
        <Link href="https://go-aheadnordic.no/trafikk-ruter-linjer/trafikkmeldinger">
          Go-Ahead trafikkmeldinger
        </Link>
      </ListItem>
      <ListItem>
        <Link href="https://www.sj.no/trafikk/">SJ Nord trafikkstatus</Link>
      </ListItem>
      <ListItem>
        <Link href="https://www.banenor.no/reise-og-trafikk/trafikkmeldinger/">
          Bane NOR trafikkmeldinger
        </Link>
      </ListItem>
    </UnorderedList>
  </>
);

const IncidentFooter = () => (
  <FullWidthBlock
    style={{
      marginBottom: '-5rem',
      marginTop: '4rem',
    }}
    className="incidents__footer"
  >
    <div className="incidents__footer__image">
      <StaticImage
        alt=""
        src="../../images/Reisende/Trafikkmeldinger/Man checking the time.svg"
      />
    </div>
    <div className="incidents__footer__text">
      <Heading4>
        Er reisen din forsinket og du trenger å endre billetten?
      </Heading4>
      <Paragraph>
        Hvis du har bestilt gjennom Entur, kan du enkelt gjøre endringer eller
        søke om refusjon i Entur-appen.
      </Paragraph>
      <Paragraph>
        Trenger du hjelp? Vi hjelper deg gjerne! Ta kontakt med oss på telefon{' '}
        <Link href="tel:+4761279088">61 27 90 88</Link>.
      </Paragraph>
    </div>
  </FullWidthBlock>
);

const IncidentCard = (props: Incident) => {
  const {
    incidentStatus,
    incidentArea,
    incidentDescription,
    routes,
    incidentLinks,
    _updatedAt,
    incidentSolved,
  } = props;
  const incidentMap: IconMap = {
    open: {
      icon: React.Fragment,
      title: 'Åpen',
    },
    closed: {
      icon: ValidationErrorFilledIcon,
      title: 'Stengt',
      color: 'var(--components-alert-alertbox-negative-standard-icondefault)',
    },
    partial: {
      icon: ValidationExclamationFilledIcon,
      title: 'Redusert hastighet',
      color: 'var(--components-form-feedbacktext-warning-standard-icon-fill)',
    },
    custom: {
      icon: ValidationExclamationFilledIcon,
      title: incidentStatus?.customStatusType ?? 'Hendelse',
      color: 'var(--components-form-feedbacktext-warning-standard-icon-fill)',
    },
  };

  // Override status when solved
  const incidentStatusMap = {
    ...incidentMap[incidentStatus.predefinedStatusType],
    ...(incidentSolved && { icon: React.Fragment }),
  };

  const Icon = incidentStatusMap?.icon;
  return (
    <BaseCard
      as="section"
      className={classNames('incidents__card', {
        'incidents__card--solved': incidentSolved,
      })}
    >
      <div className="incidents__card__header">
        <div className="incidents__card__header__text">
          {!incidentSolved ? (
            <div style={{ display: 'flex' }}>
              <span className="incidents__card__header__text__date">
                Oppdatert {formatDate(_updatedAt)}
              </span>
              {!incidentSolved && (
                <div className="incidents__card__header__tags">
                  {incidentDescription?.railReplacementBus && (
                    <Tag className="incidents__card__header__tags__tag">
                      Buss for tog
                    </Tag>
                  )}
                </div>
              )}
            </div>
          ) : (
            <Badge variant="success" style={{ marginBottom: '0.5rem' }}>
              Hendelsen er løst
            </Badge>
          )}
          <Heading3 className="incidents__card__header__text__title">
            {Icon !== undefined && (
              <Icon
                role="img"
                className="incidents__card__icon"
                aria-hidden="true"
                color={incidentStatusMap?.color}
                {...incidentStatusMap.props}
              />
            )}
            {incidentStatusMap?.title}
            {incidentArea?.isArea ? (
              <span className="incidents__card__header__text__title--location">
                <MapPinIcon />
                {incidentArea?.area}
              </span>
            ) : (
              <TravelHeader
                as={'span'}
                size="medium"
                from={incidentArea?.positionFrom ?? ''}
                to={incidentArea?.positionTo ?? ''}
              />
            )}
          </Heading3>
        </div>
      </div>
      <div className="incidents__card__content">
        {incidentDescription?.incidentDescriptionContent && (
          <PortableText
            disableScrollbarFix
            blocks={incidentDescription?.incidentDescriptionContent}
          />
        )}
        {!incidentSolved && incidentLinks !== undefined && (
          <ExpandableText
            titleElement="Heading4"
            title="Vis detaljer"
            className="incidents__card__content__details"
          >
            {routes !== undefined && routes !== null && (
              <div className="incidents__card__content__routes">
                <Heading5>Linjer som kan være påvirket:</Heading5>
                {routes?.map((routeObj, index) => (
                  <div
                    className="incidents__card__content__routes__route"
                    key={`${routeObj?.transportType ?? ''}${
                      routeObj?.routeId ?? ''
                    }${routeObj?.routeName ?? ''}${index}`}
                  >
                    <TravelTag transport={routeObj?.transportType}>
                      {routeObj?.routeId}
                    </TravelTag>
                    <SubParagraph>{routeObj?.routeName}</SubParagraph>
                  </div>
                ))}
              </div>
            )}
            <div className="incidents__card__content__details__links">
              <Heading5>For mer informasjon, besøk disse lenkene:</Heading5>
              {incidentLinks?.map((link, index) => (
                <Link href={link?.linkAdress} key={link?.linkAdress} external>
                  {link?.linkName}
                </Link>
              ))}
            </div>
          </ExpandableText>
        )}
      </div>
    </BaseCard>
  );
};

function formatDate(dateString: string) {
  const date = new Date(dateString);

  const dayMonth = date.toLocaleString('nb-NO', {
    day: 'numeric',
    month: 'long',
  });
  const year = date.toLocaleString('nb-NO', {
    year: 'numeric',
  });
  const time = date.toLocaleString('nb-NO', {
    hour: '2-digit',
    minute: '2-digit',
  });

  return `${dayMonth}, kl. ${time}`;
}

export default Trafikkmeldinger;

export function Head() {
  const location = useLocation();

  const isDraftPreview = location.search === '?draftPreview=true';
  if (isDraftPreview) return <meta name="robots" content="noindex" />;
}
